import React, { useEffect, useState } from 'react';
import { Button } from '@material-tailwind/react';
import { ModalX } from '../../../components';
import LocationPicker from '../../maintenance/components/LocationPicker';

/**
 * Sets a support ticket's location. Used in two places:
 *
 *  - standalone, from the ticket detail page, to set or amend the location
 *  - as a gate, when a status change is blocked because the ticket has no location yet
 *
 * The parent owns what happens on confirm, because the two cases differ: the standalone
 * edit saves the location on its own, while the gate hands it to the status mutation so
 * location and status are written in one request.
 *
 * LocationPicker is imported from the Maintenance module — the same cross-module import
 * Inventory's AddTransactionModal already uses. It isn't Maintenance-specific.
 */
const TicketLocationModal = ({
    isOpen,
    onClose,
    ticket,
    onConfirm,
    isLoading = false,
    title = 'Set Ticket Location',
    description,
    confirmLabel = 'Save Location',
}) => {
    const [location, setLocation] = useState({ branch: '', building: '', floor: '', space: '' });

    useEffect(() => {
        if (!isOpen) return;
        // Seed from the ticket so amending an existing location doesn't start blank.
        // Values arrive populated (objects) from the API, so fall back to the raw id.
        setLocation({
            branch: ticket?.branch?._id || ticket?.branch || '',
            building: ticket?.building?._id || ticket?.building || '',
            floor: ticket?.floor?._id || ticket?.floor || '',
            space: ticket?.space || '',
        });
    }, [isOpen, ticket]);

    // Floor is the meaningful unit — it's what the dashboard groups by. Branch and
    // building are implied by it, and Space is an optional free-text refinement.
    const canSave = Boolean(location.floor);

    const handleChange = (patch) => setLocation((prev) => {
        const next = { ...prev, ...patch };
        // Cascading: changing a parent invalidates its children.
        if (patch.branch !== undefined && patch.branch !== prev.branch) {
            next.building = '';
            next.floor = '';
        }
        if (patch.building !== undefined && patch.building !== prev.building) {
            next.floor = '';
        }
        return next;
    });

    return (
        <ModalX isOpen={isOpen} onClose={onClose} title={title} maxWidth="md">
            <div className="space-y-4">
                {description && <p className="text-sm text-blue-gray-600">{description}</p>}

                <LocationPicker
                    branch={location.branch}
                    building={location.building}
                    floor={location.floor}
                    space={location.space}
                    onChange={handleChange}
                    disabled={isLoading}
                    autoSelectSingleBranch
                />

                <p className="text-xs text-blue-gray-500">
                    Location is internal only — it is never shown to the customer.
                </p>

                <div className="flex justify-end gap-2 border-t pt-4">
                    <Button variant="outlined" onClick={onClose} disabled={isLoading}>Cancel</Button>
                    <Button onClick={() => onConfirm(location)} disabled={!canSave || isLoading}>
                        {isLoading ? 'Saving...' : confirmLabel}
                    </Button>
                </div>
            </div>
        </ModalX>
    );
};

export default TicketLocationModal;
